import type { DefaultContext } from '@apollo/client';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { LocalState } from '@apollo/client/local-state';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import {
  AuthAudience,
  generateRandomString,
  getAuthCookieNames,
  HttpHeaderNames,
  isSecureCookieBrowserContext,
} from '@repo/common';
import { SIGNED_DISPLAY_URL_POLL_INTERVAL_MS, signedDisplayUrlTypePolicies } from '@repo/frontend-shared/graphql';
import { createLogger } from '@repo/frontend-shared/utils';
import { createAuthErrorLink } from '@repo/frontend-shared/utils/create-auth-error-link';
import { getCookie } from '@repo/ui/utils/cookie';

import { fetchMoreConfig } from './config';

const gqlLogger = createLogger('admin-graphql');
const audience = AuthAudience.Admin;
const cookieNames = getAuthCookieNames(audience, isSecureCookieBrowserContext());

const httpLink = new HttpLink({
  uri: __GRAPHQL_URI__,
  credentials: 'include',
  headers: {
    [HttpHeaderNames.XRequestId]: `desktop:${generateRandomString(20)}`,
  },
});

const wsUri = __GRAPHQL_URI__.startsWith('https')
  ? __GRAPHQL_URI__.replace('https', 'wss')
  : __GRAPHQL_URI__.replace('http', 'ws');

const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUri,
  }),
);

const authLink = new SetContextLink(async (previousContext: DefaultContext) => {
  const csrfToken = getCookie(cookieNames.csrfToken);

  return {
    headers: {
      ...(previousContext.headers as Record<string, string>),
      [HttpHeaderNames.CsrfToken]: csrfToken || '',
    },
  };
});

const authErrorLink = createAuthErrorLink({
  apiUri: __API_URI__,
  getCookie,
  logger: gqlLogger,
  audience,
});

const FETCH_POLICY_CACHE_FIRST = 'cache-first';
const splitLink = ApolloLink.split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return def.kind === 'OperationDefinition' && def.operation === 'subscription';
  },
  wsLink,
  ApolloLink.from([...authErrorLink, authLink, httpLink]),
);

export const graphQLClient = new ApolloClient({
  link: splitLink,

  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: fetchMoreConfig,
      },
      Course: {
        fields: {
          picture: { merge: true },
        },
      },
      EmailTemplate: {
        fields: {
          primaryMentor: { merge: true },
        },
      },
      ...signedDisplayUrlTypePolicies,
    },
  }),

  defaultOptions: {
    watchQuery: {
      fetchPolicy: FETCH_POLICY_CACHE_FIRST,
      pollInterval: SIGNED_DISPLAY_URL_POLL_INTERVAL_MS,
      notifyOnNetworkStatusChange: false,
    },
    query: {
      fetchPolicy: FETCH_POLICY_CACHE_FIRST,
    },
  },

  localState: new LocalState({}),
});
